import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { Divider, Segment } from "semantic-ui-react";
import Pager from "../../../models/pager";
import AgenciesService from "../../../services/agencies";
import RetargetingSegmentsService from "../../../services/retargeting-segments";
import AppNotification from "../../common/notification-banner/app-notification";
import RetargetingSegmentGrid from "../components/RetargetingSegmentGrid";
import RetargetingSegmentsContext from "./context";

const pager = new Pager();
const initialState = {
  items: [],
  filter: { title: "" },
  totalPages: 0,
};

const RetargetingSegmentsPage = ({ history }) => {
  const intl = useIntl();
  const [state, setState] = useState(initialState);
  const [gridLoading, setGridLoading] = useState(false);

  const services = useRef(
    new Map([
      ["retargetingSegments", new RetargetingSegmentsService()],
      ["agencies", new AgenciesService()],
    ]),
  );
  const agency = services.current.get("agencies").getSelectedAgency() || 0;
  const message = history?.location?.state?.action || null;

  const getRetargetingSegments = useCallback(async (params) => {
    setGridLoading(true);
    try {
      const response = await services.current.get("retargetingSegments").getRetargetingSegments(params);
      // sort data by ID (DESC)
      const sortedData = response.data.sort((a, b) => b.id - a.id) || [];
      setState((prev) => ({
        ...prev,
        items: sortedData,
        totalPages: Math.ceil(response.meta.total_count / pager.limit),
      }));
      pager.setTotal(response.meta);
    } catch (error) {
      console.error("Error fetching Brand and SKU segments:", error);
    } finally {
      setGridLoading(false);
    }
  }, []);

  useEffect(() => {
    const params = { agency_id: agency, ...pager.toJson() };
    if (state.filter.title) {
      params.name_search = state.filter.title;
    }
    getRetargetingSegments(params);
  }, [getRetargetingSegments, agency, state.filter.title]);

  const getPage = (_e, { activePage }) => {
    pager.setPage(activePage);
    const params = { agency_id: agency, ...state.filter, ...pager.toJson() };
    getRetargetingSegments(params);
  };

  const updateFilters = (data) => {
    setState((prev) => ({
      ...prev,
      filter: { ...prev.filter, ...data },
    }));
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: updateFilters changes on every render
  const contextValue = useMemo(
    () => ({
      state,
      updateFilters,
      getPage,
      pager,
    }),
    [state, pager],
  );

  return (
    <Segment
      basic
      style={{ padding: "0" }}
    >
      {message === "created" && (
        <AppNotification isSuccess={true}>
          {intl.formatMessage({
            id: "MESSAGE_BRAND_SKU_SEGMENT_CREATED",
            defaultMessage: "Brand and SKU segment created successfully.",
          })}
        </AppNotification>
      )}
      {message === "updated" && (
        <AppNotification isSuccess={true}>
          {intl.formatMessage({
            id: "MESSAGE_BRAND_SKU_SEGMENT_UPDATED",
            defaultMessage: "Brand and SKU segment updated successfully.",
          })}
        </AppNotification>
      )}
      <h1>
        {intl.formatMessage({
          id: "HEADING_AUDIENCES",
          defaultMessage: "Audiences",
        })}
      </h1>
      <Divider hidden />
      <RetargetingSegmentsContext.Provider value={contextValue}>
        <RetargetingSegmentGrid
          items={state.items}
          loading={gridLoading}
        />
      </RetargetingSegmentsContext.Provider>
    </Segment>
  );
};

export default RetargetingSegmentsPage;
